import IconCheckboxOutlineBlank from 'assets/icons/checkbox-outline-blank.svg';
import IconCheckbox from 'assets/icons/checkbox.svg';
import cn from 'classnames';
import Divider from 'components/divider';
import Toggle from 'components/toggle';
import {
  InputProductType,
  WetFoodTagValues,
} from 'interfaces/api-input/input-product-type';
import { DraftState } from 'modules/landingpage/components/need-capture/state/interface';
import { AnimatePresence, m } from 'motion/react';
import Image from 'next/image';
import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';

const ANIMATION_CONFIG = {
  duration: 0.25,
  ease: 'easeInOut',
} as const;

const activeInputProductType = {
  [InputProductType.WetFood]: InputProductType.WetFood,
  [InputProductType.DryFood]: InputProductType.DryFood,
  [InputProductType.Snacks]: InputProductType.Snacks,
} as const;

interface Props
  extends React.DetailedHTMLProps<
    React.LabelHTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  selectedOption: DraftState;
  onSelectFoodTypes: (value: InputProductType) => void;
  onSelectWetFoodTypes: (values: WetFoodTagValues[]) => void;
}

/**
 * It renders a list of food types that the user can toggle.
 */
const FoodTypeSelector: React.FC<Props> = ({
  selectedOption,
  className,
  onSelectFoodTypes,
  onSelectWetFoodTypes,
  ...divProps
}) => {
  const { draftFoodTypes, draftWetFoodTypes } = selectedOption;
  const handleWetFoodTypeChange = (value: WetFoodTagValues): void => {
    const isWetFoodSelected = draftFoodTypes?.includes(
      InputProductType.WetFood
    );
    const isCurrentlySelected = draftWetFoodTypes?.includes(value);

    if (isWetFoodSelected) {
      // If wet food is selected, toggle the individual type
      const newSelection = isCurrentlySelected
        ? draftWetFoodTypes?.filter((type) => type !== value) || []
        : [...(draftWetFoodTypes || []), value];
      onSelectWetFoodTypes(newSelection);

      // If all wet food types are deselected, deselect WetFood option
      if (newSelection.length === 0) {
        onSelectFoodTypes(InputProductType.WetFood);
      }
    }
  };

  const handleFoodTypeToggle = (value: InputProductType): void => {
    onSelectFoodTypes(value);

    // Handle automatic selection/deselection of wet food types
    if (value === InputProductType.WetFood) {
      const isWetFoodSelected = !draftFoodTypes?.includes(value);
      if (isWetFoodSelected) {
        // When wet food is selected, select all wet food types
        onSelectWetFoodTypes(Object.values(WetFoodTagValues));
      } else {
        // When wet food is deselected, unselect all wet food types
        onSelectWetFoodTypes([]);
      }
    }
  };

  return (
    <div
      className={cn('mt-2', className)}
      data-test="food-type-selector"
      {...divProps}
    >
      <span className="text-text-light-bg-primary mb-2 block font-medium">
        <FormattedMessage id="pet-profile:feed-type-selector:title" />
      </span>
      <div className="flex flex-col gap-4">
        {Object.entries(activeInputProductType).map(([key, value]) => (
          <React.Fragment key={key}>
            <div
              className="flex items-center justify-between text-sm"
              data-test={`food-type-${value}-container`}
            >
              <div className="flex items-center gap-2.5">
                <Image
                  src={`/images/pet-profile/food_type_${value}.png`}
                  alt={`food-type-${value}-icon`}
                  width={40}
                  height={40}
                  data-test={`food-type-${value}-icon`}
                />

                <span
                  className="text-text-light-bg-primary block font-medium"
                  data-test={`food-type-${value}-label`}
                >
                  <FormattedMessage
                    id={`pet-profile:feed-type-selector:${key}`}
                  />
                </span>
              </div>

              <Toggle
                isChecked={draftFoodTypes?.includes(value) ?? false}
                onChange={() => handleFoodTypeToggle(value)}
                showIcon={false}
                dataTest={`food-type-${value}-toggle`}
              />
            </div>
            {value === InputProductType.WetFood && (
              <AnimatePresence>
                {draftFoodTypes?.includes(value) && (
                  <m.div
                    className="relative -mt-px pl-12"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={ANIMATION_CONFIG}
                    data-test="wet-food-options-container"
                  >
                    {Object.entries(WetFoodTagValues).map(
                      ([key, value], index, array) => (
                        <React.Fragment key={key}>
                          <label
                            className="text-text-light-bg-secondary flex cursor-pointer items-center text-sm font-normal"
                            data-test={`wet-food-type-${value}-option`}
                          >
                            <input
                              type="checkbox"
                              name="wetFoodType"
                              value={value}
                              checked={draftWetFoodTypes?.includes(value)}
                              onChange={() => handleWetFoodTypeChange(value)}
                              className="sr-only"
                              aria-label={`Select ${key} as wet food type`}
                              data-test={`wet-food-type-${value}-checkbox`}
                            />
                            <div
                              className="relative mr-2 h-6 w-6"
                              data-test={`wet-food-type-${value}-checkbox-icon`}
                            >
                              {draftWetFoodTypes?.includes(value) ? (
                                <IconCheckbox className="text-text-light-bg-primary absolute inset-0 h-full w-full" />
                              ) : (
                                <IconCheckboxOutlineBlank className="text-text-light-bg-primary absolute inset-0 h-full w-full" />
                              )}
                            </div>
                            <div className="flex flex-col text-sm">
                              <span
                                className="text-text-light-bg-primary font-medium"
                                data-test={`wet-food-type-${value}-label`}
                              >
                                <FormattedMessage
                                  id={`pet-profile:feed-type-selector:wet_food:${value}`}
                                />
                              </span>

                              <span
                                className="leading-4.5"
                                data-test={`wet-food-type-${value}-description`}
                              >
                                <FormattedMessage
                                  id={`pet-profile:feed-type-selector:wet_food:description:${value}`}
                                />
                              </span>
                            </div>
                          </label>
                          {index < array.length - 1 && (
                            <Divider
                              data-test={`wet-food-type-${value}-divider`}
                            />
                          )}
                        </React.Fragment>
                      )
                    )}
                  </m.div>
                )}
              </AnimatePresence>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default memo(FoodTypeSelector);
